import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useModal } from '../context/ModalContext';
import Icon from './Icon';
import '../styles/NotificationsPanel.css';

const NotificationsPanel = ({ user }) => {
  const modal = useModal();

  // Email notification preferences
  const [emailPreferences, setEmailPreferences] = useState({
    slack: {
      todoItems: false,
      contentItems: false,
      ideas: false
    },
    email: {
      todoItems: false,
      contentItems: false,
      ideas: false
    }
  });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Test notification states
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  const [checkingSlack, setCheckingSlack] = useState(true);

  useEffect(() => {
    loadNotificationPreferences();
    checkSlackStatus();
  }, []);

  const checkSlackStatus = async () => {
    try {
      const response = await axios.get('/api/slack/status');
      setSlackConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Slack status:', error);
      setSlackConnected(false);
    } finally {
      setCheckingSlack(false);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await axios.get('/api/notification-preferences');
      if (response.data.preferences) {
        // Only update if we have actual saved preferences
        // Otherwise keep the default state (all false)
        setEmailPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      // On error, keep default state (all toggles off)
    } finally {
      setPreferencesLoaded(true);
    }
  };

  const saveNotificationPreferences = async () => {
    try {
      await axios.put('/api/notification-preferences', {
        preferences: emailPreferences
      });
      modal.showSuccess('Notification preferences saved successfully!');
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      modal.showError('Failed to save notification preferences');
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await axios.post('/api/notifications/test-email');
      modal.showSuccess('Test email notification sent! Check your inbox.');
    } catch (error) {
      console.error('Failed to send test email:', error);
      modal.showError(error.response?.data?.error || 'Failed to send test email notification');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSlack = async () => {
    setTestingSlack(true);
    try {
      await axios.post('/api/slack/test');
      modal.showSuccess('Test Slack notification sent! Check your Slack DMs.');
    } catch (error) {
      console.error('Failed to send test Slack notification:', error);
      modal.showError(error.response?.data?.error || 'Failed to send test Slack notification');
    } finally {
      setTestingSlack(false);
    }
  };

  return (
    <div className="notifications-panel">
      {/* Notification Preferences */}
      <div className="notifications-section">
        <div className="notification-preferences">
          <p className="preferences-intro">Choose which types of notifications you'd like to receive via email and Slack.</p>

          {!preferencesLoaded ? (
            <div className="preferences-loading">Loading preferences...</div>
          ) : (
            <>
            <div className="preferences-table">
            <div className="preferences-header">
              <div className="preference-label-header">Notification Type</div>
              <div className="preference-channel-header">Slack</div>
              <div className="preference-channel-header">Email</div>
            </div>

            <div className="preference-row">
              <div className="preference-label">
                <Icon name="list" size="1rem" />
                <span>To Do Items</span>
              </div>
              <div className="preference-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailPreferences.slack.todoItems}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      slack: { ...prev.slack, todoItems: e.target.checked }
                    }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="preference-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailPreferences.email.todoItems}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      email: { ...prev.email, todoItems: e.target.checked }
                    }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="preference-row">
              <div className="preference-label">
                <Icon name="pencil" size="1rem" />
                <span>Content Items</span>
              </div>
              <div className="preference-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailPreferences.slack.contentItems}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      slack: { ...prev.slack, contentItems: e.target.checked }
                    }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="preference-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailPreferences.email.contentItems}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      email: { ...prev.email, contentItems: e.target.checked }
                    }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="preference-row">
              <div className="preference-label">
                <Icon name="lightbulb" size="1rem" />
                <span>Ideas</span>
              </div>
              <div className="preference-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailPreferences.slack.ideas}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      slack: { ...prev.slack, ideas: e.target.checked }
                    }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="preference-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailPreferences.email.ideas}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      email: { ...prev.email, ideas: e.target.checked }
                    }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="preferences-actions">
            <button className="save-preferences-btn" onClick={saveNotificationPreferences}>
              Save Preferences
            </button>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Test Notifications */}
      <div className="notifications-section">
        <div className="test-notifications">
          <h3>Test Notifications</h3>
          <p className="test-intro">Send test notifications to verify your settings are working correctly.</p>
          <div className="test-actions">
            <button
              className="test-notification-btn test-email-btn"
              onClick={handleTestEmail}
              disabled={testingEmail}
            >
              <Icon name="paper-plane-empty" size="1rem" />
              <span>{testingEmail ? 'Sending...' : 'Test Email Notification'}</span>
            </button>
            <button
              className="test-notification-btn test-slack-btn"
              onClick={handleTestSlack}
              disabled={testingSlack || !slackConnected || checkingSlack}
              title={!slackConnected && !checkingSlack ? 'Connect Slack in your Profile to test notifications' : ''}
            >
              <Icon name="bell" size="1rem" />
              <span>{testingSlack ? 'Sending...' : 'Test Slack Notification'}</span>
            </button>
          </div>
          {!slackConnected && !checkingSlack && (
            <p className="slack-connection-hint">
              <Icon name="info-circled" size="0.875rem" />
              <span>Connect Slack in your Profile to enable Slack notification testing</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPanel;
